import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { formatPhoneNumber } from '../../../../../../../app/utils/formatPhoneNumber'
import { SellerInfo } from '../../../../../../../app/entities/SellerInfo'
import { formatCpfNumber } from '../../../../../../../app/utils/cpfFormater'
import { ReactNode, SyntheticEvent } from 'react'
import { useToast } from '@/view/components/ui/use-toast'
import { Whatsapp } from 'iconsax-react'
import { Button } from '@/view/components/ui/button'
import { usePersonalInfoController } from './usePersonalInfoController'
import { useAuth } from '@/app/hooks/useAuth'
import { formatName } from '@/app/utils/formatName'
import { useNavigate } from 'react-router-dom'
import { EditCpfModal } from './EditCpfModal'
import { Edit } from 'lucide-react'
import { UserRoleEnum } from '@/app/services/usersService/user'
import { EditCommissionModal } from '../../EditCommissionModal'
import { SupervisorCommissions } from './SupervisorCommissions'


type PersonalInfoProps = {
  data: SellerInfo
}
type ItemsType = { label: string; value: string; icon?: ReactNode }

export const PersonalInfo = ({ data }: PersonalInfoProps) => {
  const { toast } = useToast()
  const { isSuperAdmin, isSupport, isAdmin } = useAuth()
  const navigate = useNavigate()

  const items: ItemsType[] = [
    {
      label: 'Email',
      value: data.email,
    },
    {
      label: 'Instagram',
      value: data.instagram ? `@${data.instagram}` : 'Não informado',
    },
    {
      label: 'Número de telefone',
      value: formatPhoneNumber(data.phone),
      icon: (
        <Whatsapp
          className="cursor-pointer text-green-500 hover:text-green-600"
          onClick={() => window.open(`https://wa.me/${data.phone}`)}
        />
      ),
    },
    {
      label: 'RG',
      value: data.rg?.number || (data.rg as unknown as string),
    },
    {
      label: 'Data de cadastro',
      value: data.createdAt
        ? format(new Date(data.createdAt), "dd 'de' MMMM 'de' yyyy", {
            locale: ptBR,
          })
        : '-',
    },
    {
      label: 'Indicado por',
      value: data.referral
        ? data.referral.type === 'user'
          ? (formatName(data.referral.name) as string)
          : data.referral.name
        : '-',
      icon: data.referral ? (
        data.referral.type === 'user' ? (
          <i
            className="fa-solid fa-chalkboard-user"
            onClick={() => navigate(`/vendedores/${data.referral?._id}`)}
          />
        ) : (
          <i
            className="fa-solid fa-people-group"
            onClick={() => navigate(`/times/detalhes/${data.referral?._id}`)}
          />
        )
      ) : undefined,
    },
  ]

  const unicoUrl = `${import.meta.env.VITE_VALIDACAO_URL}${data._id}`

  const copyLink = async (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const type = 'text/plain'
    const blob = new Blob([unicoUrl], { type })
    const data = [new ClipboardItem({ [type]: blob })]
    await navigator.clipboard.write(data)

    toast({ variant: 'default', title: 'Link copiado' })
  }

  const copy = async (s: string) => {
    try {
      await navigator.clipboard.writeText(s)
      toast({
        title: 'Copiado!',
      })
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  let hasUnicoApproved = false

  data.unico?.forEach(({ approved }) => {
    if (approved) hasUnicoApproved = true
  })

  const {
    handleUnicoApprove,
    isPending,
    isCpfEdit,
    setIsCpfEdit,
    isCpfModalOpen,
    setIsCpfModalOpen,
    isCommissionModalOpen,
    setIsCommissionModalOpen,
  } = usePersonalInfoController()

  return (
    <div>
      <h2 className="my-5 font-semibold">Sobre</h2>
      {isAdmin ? (
        <div
          className="mb-2 flex cursor-pointer items-center gap-2"
          onMouseEnter={() => setIsCpfEdit(true)}
          onMouseLeave={() => setIsCpfEdit(false)}
          onClick={() => copy(data.cpf)}
        >
          <p className="text-sm text-black04">CPF</p>
          <p className="text-sm font-medium">{formatCpfNumber(data.cpf)}</p>
          {isCpfEdit && (
            <Edit
              onClick={() => setIsCpfModalOpen(true)}
              size={16}
              className="text-gray-500 hover:text-gray-700"
            />
          )}
        </div>
      ) : (
        <div className="mb-2 flex items-center gap-2">
          <p className="text-sm text-black04">CPF</p>
          <p className="text-sm font-medium">{formatCpfNumber(data.cpf)}</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map(({ label, value, icon }) => (
          <div className="flex items-center gap-2" key={`${label}-${value}`}>
            <p className="text-sm text-black04">{label}</p>
            <p className="text-sm font-medium">{value}</p>
            {icon}
          </div>
        ))}
      </div>

      {data?.unico && (
        <>
          <div className="my-5 flex items-center gap-5">
            <h2 className="font-semibold">Unico</h2>
            {!hasUnicoApproved && (isSuperAdmin || isSupport || isAdmin) && (
              <Button
                className="rounded-full bg-primary text-xs font-bold text-white01"
                onClick={handleUnicoApprove}
                disabled={isPending}
              >
                Aprovar
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {data.unico.map(({ approved, processId }) => (
              <div className="flex items-center gap-2" key={processId}>
                <p className="text-sm text-black04">Processo:</p>
                <p className="text-sm font-medium">{processId}</p>
                <span>
                  {approved ? (
                    <i className="fa-solid fa-circle-check text-sm text-success" />
                  ) : (
                    <i className="fa-solid fa-circle-exclamation text-error" />
                  )}
                </span>
              </div>
            ))}
          </div>
          <>
            {!hasUnicoApproved && (
              <div
                className="flex items-baseline justify-start gap-2"
                onClick={copyLink}
              >
                <h2 className="text-md mt-5 font-medium text-blue-600 ">
                  Link para escaneamento
                </h2>
                <div className="cursor-pointer rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary hover:no-underline">
                  Copiar
                </div>
              </div>
            )}
          </>
        </>
      )}

      {isAdmin && data?.role === UserRoleEnum.SUPERVISOR && (
        <SupervisorCommissions 
          commissions={data.commissionsV2} 
          onEdit={() => setIsCommissionModalOpen(true)} 
        />
      )} 

      <EditCommissionModal
        open={isCommissionModalOpen}
        onClose={() => setIsCommissionModalOpen(false)}
        sellerId={data._id}
        commissions={data.commissionsV2}
      />

      <EditCpfModal
        open={isCpfModalOpen}
        onClose={() => setIsCpfModalOpen(false)}
        sellerId={data._id}
      />
    </div>
  )
}
